import React, {ChangeEvent, useEffect, useRef, useState} from 'react';
import {Button, Flex, Form, Input, type InputRef, Modal} from "antd";
import CustomButton from "../../../UI/CustomButton/CustomButton";
import { ReactComponent as CrossIcon } from '../../../../assets/images/global-icons/cross_ic.svg';
import { ReactComponent as ArrowIcon } from '../../../../assets/images/global-icons/arrow_ic.svg';
import './EditPhoneNumberModal.scss'
import {useForm} from "antd/lib/form/Form";
import styles from "../../../../pages/SignUp/Personal/PhoneVerify/PhoneVerify.module.scss";
import {
  useGetMeQuery,
  useUpdateProfileMutation,
  useVerifyCodeMutation,
  useVerifyPhoneMutation
} from "../../../../redux/API/userSlice";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";

interface EditPhoneNumberModalInterface {
  visible: boolean,
  onClose: () => void,
  userId: string | undefined,
  phone?: string,
  setIsChangePhoneToastOpen: (value: boolean) => void
}

const EditPhoneNumberModal = ({visible, onClose, userId, phone, setIsChangePhoneToastOpen}: EditPhoneNumberModalInterface) => {
  const [verificationStep, setVerificationStep] = useState(1);
  const [phoneValue, setPhone] = useState('');
  const [timer, setTimer] = useState(60);
  const [isButtonDisable, setIsButtonDisable] = useState(true);
  const [canResend, setCanResend] = useState(false);
  const [code, setCode] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);
  const inputRefs = useRef<(InputRef | null)[]>([]);
  const [form] = useForm();
  const {isAuth} = useSelector((state: RootState) => state.isAuth);

  const [verifyPhone] = useVerifyPhoneMutation();
  const [verifyCode] = useVerifyCodeMutation();
  const [updateUser] = useUpdateProfileMutation();

  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, "").slice(-10);
    if (digits.length < 3) return `+1 ${digits}`;
    if (digits.length < 6) return `+1 ${digits.slice(0, 3)} ${"*".repeat(digits.length - 3)}`;
    return `+1 ${digits.slice(0, 3)} ${"*".repeat(6)}${digits.slice(-2)}`;
  };

  const handlePhoneClick = async () => {
    try {
      await verifyPhone({
        body: {
          countryCode: '1',
          phone: phoneValue,
        },
        accessToken: isAuth
      }).unwrap();

      setVerificationStep(2);
    } catch (error) {
      console.error(error);
    }
  }

  const handleCodeClick = async () => {
    try {
      await verifyCode({
        body: {
          countryCode: '1',
          phone: phoneValue,
          otp: code.join(''),
        },
        accessToken: isAuth
      });

      await updateUser({
        id: userId || '',
        accessToken: isAuth,
        body: {
          phone: form.getFieldValue('phone')
        }
      })

      setCode(['', '', '', '']);
      setIsChangePhoneToastOpen(true);
      setVerificationStep(1);
      onClose();
    } catch (error) {
      console.error(error);
    }
  }

  const resendCode = async () => {
    setTimer(60);
    setCanResend(false);

    try {
      await verifyPhone({
        body: {
          countryCode: '1',
          phone: phoneValue,
        },
        accessToken: isAuth
      }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPosition = input.selectionStart || 0;
    const previousValue = input.value;

    // Отримуємо тільки цифри
    let inputValue = previousValue.replace(/\D/g, '');

    if (inputValue.length > 11) {
      inputValue = inputValue.slice(0, 11);
    }

    // Форматуємо значення
    let formattedValue = '';
    if (inputValue.length > 0) {
      formattedValue += inputValue[0];
    }

    if (inputValue.length > 1) {
      formattedValue += ` (${inputValue.slice(1, 4)}`;
    }

    if (inputValue.length > 4) {
      formattedValue += `) ${inputValue.slice(4, 7)}`;
    }

    if (inputValue.length > 7) {
      formattedValue += `-${inputValue.slice(7, 11)}`;
    }

    // Обчислюємо нову позицію курсору
    let newCursorPosition = cursorPosition;

    // Підраховуємо скільки цифр було до курсору
    const charsBeforeCursor = previousValue.slice(0, cursorPosition);
    const digitsBeforeCursor = charsBeforeCursor.replace(/\D/g, '').length;

    // Знаходимо нову позицію курсору після форматування
    let digitCount = 0;
    newCursorPosition = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitCount++;
      }
      if (digitCount === digitsBeforeCursor) {
        newCursorPosition = i + 1;
        break;
      }
    }

    form.setFieldValue('phone', formattedValue);
    setPhone(inputValue);

    // Встановлюємо курсор в правильну позицію після оновлення DOM
    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    setIsButtonDisable(inputValue.length !== 11);
  };


  useEffect(() => {
    if (verificationStep === 2) {
      setTimer(60);
    }
  }, [verificationStep]);

  const setInputRef = (el: InputRef | null, index: number) => {
    if (el) inputRefs.current[index] = el;
  };

  const handleChange = (value: string, index: number) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);

    if (pastedText.length > 0) {
      const newCode = [...code];
      pastedText.split("").forEach((char, index) => {
        newCode[index] = char;
      });

      setCode(newCode);
      const nextIndex = pastedText.length < 4 ? pastedText.length : 3;
      inputRefs.current[nextIndex]?.focus();
    }
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable={true}
      onCancel={onClose}
      centered
      className={'EditPhoneNumberModal'}
    >
      {
        verificationStep === 1 ?
          <Flex vertical>
            <Flex justify={"space-between"} align={"center"} className={'EditPhoneNumberModal__header'}>
              <h3>Edit phone number</h3>
              <CrossIcon color={"#000000"} onClick={() => onClose()}/>
            </Flex>
            <Flex className={'EditPhoneNumberModal__body'}>
              <Form
                form={form}
                initialValues={{
                  phone: phone
                }}
                // onFinish={handleFinish}
                layout={'vertical'}
                className={'EditPhoneNumberModal__body__form'}>
                <Form.Item
                  name={'phone'}
                  label={'Phone number'}
                >
                  <Input
                    placeholder={'phone'}
                    className={'EditPhoneNumberModal__body__form__input'}
                    maxLength={16}
                    prefix="+"
                    onChange={(e) => handlePhoneChange(e)}
                  />
                </Form.Item>

                <Form.Item>
                  <Flex className={'EditPhoneNumberModal__body__form__button'}>
                    <CustomButton text={'Save'} disable={isButtonDisable} onClick={() => handlePhoneClick()}/>
                  </Flex>
                </Form.Item>
              </Form>
            </Flex>
          </Flex>
          :
          <Flex vertical>
            <Flex justify={"space-between"} align={"center"} className={'EditPhoneNumberModal__header EditPhoneNumberModal__header__second'}>
              <ArrowIcon onClick={() => {
                setVerificationStep(1);
                form.setFieldValue('otp', '')
                setCode(["", "", "", ""]);
              }}/>
              <h3>Verification</h3>
            </Flex>
            <Flex className={'EditPhoneNumberModal__body'}>
              <Form
                form={form}
                // initialValues={initialState}
                onFinish={handleCodeClick}
                layout={'vertical'}
                className={'EditPhoneNumberModal__body__form'}>
                <p className={'EditPhoneNumberModal__body__form__title'}>Code has been send to {formatPhoneNumber(phoneValue)}</p>
                <Form.Item
                  name={'otp'}
                >
                  <Flex gap={'13px'}>
                    {code.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => setInputRef(el, index)}
                        value={digit}
                        maxLength={1}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        className={`${styles.PhoneVerify__input} ${digit ? styles.PhoneVerify__input_filled : ''} ${error ? styles.PhoneVerify__input_error : ''}`}
                      />
                    ))}
                  </Flex>
                </Form.Item>

                <Flex className={'EditPhoneNumberModal__body__form__resend'} justify="center" align="center">
                  {canResend ? (
                    <Button type="link" onClick={resendCode} className={styles.PhoneVerify__resend__button}>
                      Resend code
                    </Button>
                  ) : (
                    <p>
                      Resend code in <span style={{ color: "red" }}>{timer}</span> s
                    </p>
                  )}
                </Flex>

                <Form.Item>
                  <Flex className={'EditPhoneNumberModal__body__form__button'}>
                    <CustomButton text={'Verify'} disable={false} htmlType={'submit'}/>
                  </Flex>
                </Form.Item>
              </Form>
            </Flex>
          </Flex>
      }
    </Modal>
  );
};

export default EditPhoneNumberModal;